<!--
  @project E-School Web
  @author Vothana CHY
  @date 2026-06-30
-->

<script lang="ts" setup>
import { CONTACT_INFO } from '~/constants/contact';
import { PAGES_SEO } from '~/constants/seo';

const seoConfig = PAGES_SEO.home;

useSeoMeta({
  title: seoConfig.title,
  ogTitle: seoConfig.ogTitle,
  description: seoConfig.description,
  ogDescription: seoConfig.ogDescription,
  ogImage: seoConfig.ogImage,
  twitterCard: 'summary_large_image',
});

// Enable smart banner on home page only
useHead({
  meta: [{ name: 'smartbanner:enabled-platforms', content: 'android,ios' }],
});

useSchemaOrg([
  defineWebPage({
    '@type': 'FAQPage',
  }),
  defineOrganization({
    name: 'E-School Cambodia',
    url: 'https://e-schoolcambodia.com',
    logo: 'https://e-schoolcambodia.com/favicon-96x96.png',
    sameAs: [
      CONTACT_INFO.socials.facebook,
      CONTACT_INFO.socials.youtube,
      CONTACT_INFO.socials.telegram,
      CONTACT_INFO.socials.linkedin,
    ],
    contactPoint: [
      {
        telephone: '+85515372363',
        contactType: 'customer service',
        areaServed: 'KH',
        availableLanguage: ['Khmer', 'English'],
      },
    ],
  }),
  defineWebSite({
    name: 'E-School Cambodia',
    url: 'https://e-schoolcambodia.com',
    potentialAction: [
      {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: 'https://e-schoolcambodia.com/courses?q={search_term_string}',
        },
        'query-input': 'required name=search_term_string',
      },
    ],
  }),
  ...(seoConfig.faqs
    ? seoConfig.faqs().map((faq) =>
        defineQuestion({
          name: faq.question,
          acceptedAnswer: faq.answer,
        })
      )
    : []),
]);
</script>

<template>
  <div
    class="min-h-screen bg-white font-sans text-slate-800 selection:bg-brand-red selection:text-white dark:text-slate-100"
  >
    <!-- Main Wrapper with Full-Width Backgrounds -->
    <div class="relative flex min-h-screen flex-col overflow-x-hidden bg-white">
      <!-- 1. Top bar & Main Navigation Header -->
      <HomeHeader />

      <!-- Header Spacer to prevent layout shift with fixed header -->
      <div class="h-[72px] w-full shrink-0 md:h-[124px]"></div>

      <!-- 2. Hero Banner -->
      <HomeHero class="sticky top-[72px] z-10 md:top-[124px]" />

      <!-- Scrollable content wrapper that slides over Hero -->
      <div class="relative z-20 bg-white">
        <!-- 3. What is E-School? -->
        <HomeAbout />

        <!-- 4. Benefits Section -->
        <HomeBenefits />

        <!-- 5. Video Course Section -->
        <HomeVideoCourses />

        <!-- 6. Zoom Course Section -->
        <HomeZoomCourses />

        <!-- 7. Best Lecturers Section -->
        <HomeLecturers />

        <!-- 8. Stats Section -->
        <HomeStats />

        <!-- 9. Special Mobile Function Carousel -->
        <HomeMobileApp />

        <!-- 10. Our Partners -->
        <HomePartners />

        <!-- 11. Footer Section -->
        <HomeFooter />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* scroll-behavior: smooth is already applied globally in main.css */
</style>
